import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { wpUserDataLocal } from './userData';

const getUserCredentials = () => {
	const baseURL = process.env.NODE_ENV === 'development'
		? wpUserDataLocal.url
		: window.location.origin;

	if (process.env.NODE_ENV === 'development') {
		// In development mode, use credentials from userData.js
		return {
			user_id: wpUserDataLocal.userId || '',
			user_token: wpUserDataLocal.token || '',
			baseURL,
		};
	}

	// In production mode, get credentials from window.wpUserData set in header.php
	if (window.wpUserData) {
		return {
			user_id: window.wpUserData.userId || '',
			user_token: window.wpUserData.token || '',
			baseURL,
		};
	}

	// If no credentials are found, return empty values
	return { user_id: '', user_token: '', baseURL };
};

const getPostId = () => {
	if (process.env.NODE_ENV === 'development') {
		return 656; // Use 656 in development mode
	} else {
		const match = document.body.className.match(/postid-(\d+)/);
		return match ? parseInt(match[1], 10) : null;
	}
};

const getAddNoteButtonDisabledClass = () => {
	if (process.env.NODE_ENV === 'development') {
		return wpUserDataLocal.addNoteButtonDisabledClass;
	}

	if (window.wpUserData) {
		return window.wpUserData.addNoteButtonDisabledClass;
	}

	return '';
}

export const fetchNotes = createAsyncThunk(
	'notes/fetchNotes',
	async () => {
		const { user_id, user_token, baseURL } = getUserCredentials();

		const response = await fetch(`${baseURL}/wp-json/lipskaya/v1/notes?user_id=${user_id}&user_token=${user_token}`);
		return response.json();
	}
);

export const deleteNote = createAsyncThunk(
	'notes/deleteNote',
	async (note_id, { rejectWithValue }) => {
		try {
			const { user_id, user_token, baseURL } = getUserCredentials();

			const response = await fetch(
				`${baseURL}/wp-json/lipskaya/v1/notes?user_id=${user_id}&user_token=${user_token}&note_id=${note_id}`,
				{
					method: 'DELETE',
					headers: {
						'Content-Type': 'application/json',
					},
				}
			);

			if (!response.ok) {
				const errorData = await response.json();
				return rejectWithValue(errorData);
			}

			return await response.json();
		} catch (error) {
			return rejectWithValue(error.message);
		}
	}
);

export const createNote = createAsyncThunk(
	'notes/createNote',
	async (_, { rejectWithValue }) => {
		try {
			const { user_id, user_token, baseURL } = getUserCredentials();
			let post_id = getPostId();

			if (!post_id) {
				return rejectWithValue('Post ID is undefined');
			}

			const response = await fetch(
				`${baseURL}/wp-json/lipskaya/v1/notes?user_id=${user_id}&user_token=${user_token}&post_id=${post_id}`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: null
				}
			);

			if (!response.ok) {
				const errorData = await response.json();
				return rejectWithValue(errorData);
			}

			return await response.json(); // Returns updated notes list
		} catch (error) {
			return rejectWithValue(error.message);
		}
	}
);

export const updateNote = createAsyncThunk(
	'notes/updateNote',
	async (_, { getState, rejectWithValue }) => {
		const state = getState();
		const note_id = state.notes.editedNoteId;
		const post_id = state.notes.editedNoteContent.post_id;
		const note_content = state.notes.editedNoteContent.note;

		// Check if editedNoteId is not null
		if (!note_id) {
			return rejectWithValue('No note selected for editing');
		}

		try {
			const { user_id, user_token, baseURL } = getUserCredentials();

			if (!post_id) {
				return rejectWithValue('Post ID is undefined');
			}

			// Construct the endpoint for updating the note
			const url = `${baseURL}/wp-json/lipskaya/v1/notes?user_id=${user_id}&user_token=${user_token}&post_id=${post_id}&note_id=${note_id}`;

			// Send the update request (assuming we're updating the note text)
			const response = await fetch(url, {
				method: 'POST', // Use POST method for updating
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(note_content)
			});

			if (!response.ok) {
				const errorData = await response.json();
				return rejectWithValue(errorData);
			}

			// Return the updated list of posts or notes
			return await response.json(); // Assuming the response contains updated notes/posts
		} catch (error) {
			return rejectWithValue(error.message);
		}
	}
);

const notesSlice = createSlice({
	name: 'notes',
	initialState: {
		notes: {},
		isLoading: false,
		error: null,
		isEditing: false,
		editedNoteId: null,
		editedNoteContent: null,
		addNoteButtonDisabledClass: getAddNoteButtonDisabledClass(),
	},
	reducers: {
		updateEditedNoteContent: (state, action) => {
			state.editedNoteContent.note = action.payload;
		},
		setEditedNoteId: (state, action) => {
			state.editedNoteId = action.payload;
			state.editedNoteContent = state.notes[action.payload];
			state.isEditing = true;
		}
	},
	extraReducers: (builder) => {
		builder
			// Fetch Notes
			.addCase(fetchNotes.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(fetchNotes.fulfilled, (state, action) => {
				state.isLoading = false;
				state.notes = action.payload.notes;
			})
			.addCase(fetchNotes.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.error.message;
			})

			// Delete Note
			.addCase(deleteNote.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(deleteNote.fulfilled, (state, action) => {
				state.isLoading = false;
				state.notes = action.payload.notes;
			})
			.addCase(deleteNote.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload || action.error.message;
			})

			// Create Note
			.addCase(createNote.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(createNote.fulfilled, (state, action) => {
				state.isLoading = false;
				state.isEditing = true;
				state.editedNoteId = action.payload.note_id;
				state.editedNoteContent = action.payload.notes[action.payload.note_id];
				state.notes = action.payload.notes;
			})
			.addCase(createNote.rejected, (state, action) => {
				state.isLoading = false;
				state.isEditing = false;
				state.editedNoteId = null;
				state.error = action.payload || action.error.message;
			})

			// Update Note
			.addCase(updateNote.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(updateNote.fulfilled, (state, action) => {
				state.isLoading = false;
				state.isEditing = false;
				state.editedNoteId = null;
				state.editedNoteContent = null;
				state.notes = action.payload.notes;
			})
			.addCase(updateNote.rejected, (state, action) => {
				console.log(action);
				state.isLoading = false;
				state.isEditing = false;
				state.editedNoteId = null;
				state.error = action.payload || action.error.message;
			});
	}
});

export const {
	updateEditedNoteContent,
	setEditedNoteId
} = notesSlice.actions;

export default notesSlice.reducer;